import type { ActivityLogRow } from '@/api/customer-activity';

/**
 * Group activity-log rows by LOCAL calendar date for the full Activity Logs tab.
 * Kept separate from the mini timeline so both surfaces stay independent.
 * Uses getFullYear/getMonth/getDate — never toISOString — so the day key does
 * not shift back a day in Asia/Saigon (UTC+7).
 */

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Local `YYYY-MM-DD` grouping key. */
export function localDateKey(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** ISO → "Tue · 01 Jul 2026" group header label. */
export function groupDateLabel(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return `${WEEKDAYS[d.getDay()]} · ${String(d.getDate()).padStart(2, '0')} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

export interface ActivityDateGroup {
  key: string;
  label: string;
  rows: ActivityLogRow[];
}

/** Group rows by local date, preserving the (newest-first) API order. */
export function groupByDate(rows: ActivityLogRow[]): ActivityDateGroup[] {
  const groups: ActivityDateGroup[] = [];
  const index = new Map<string, ActivityDateGroup>();
  for (const row of rows) {
    const key = localDateKey(row.created_at);
    let group = index.get(key);
    if (!group) {
      group = { key, label: groupDateLabel(row.created_at), rows: [] };
      index.set(key, group);
      groups.push(group);
    }
    group.rows.push(row);
  }
  return groups;
}

/** Humanize an action_type slug for the filter option label. */
export function actionOptionLabel(action: string): string {
  const words = action.replace(/[_-]+/g, ' ').trim().toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Distinct action_type values present in the loaded rows, sorted for the filter. */
export function distinctActionTypes(rows: ActivityLogRow[]): string[] {
  const set = new Set<string>();
  for (const row of rows) {
    if (row.action_type) set.add(row.action_type);
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}
